import React, { useState } from "react";

import {
  DealComponentPaymentResponseDto,
  DealResponseDto,
} from "../../../types/types";
import type { DealFormData } from "../createDeal/dealFormTypes";
import type { TranslationFn } from "../createDeal/dealFormFields";
import { asRecord } from "../../../utils/typeGuards";
import { InformationCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { FaSpinner } from "react-icons/fa";

const toTitleCase = (str: string): string =>
  str
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const normalizeTimingKey = (timing?: string): string => {
  const key = String(timing || "").toLowerCase();
  if (key === "pre_event" || key === "pre_match") return "pre_event";
  if (key === "post_event" || key === "post_match") return "post_event";
  return key || "single";
};

const resolvePaymentTiming = (payment: unknown): string => {
  const paymentRecord = asRecord(payment);
  const componentRecord = asRecord(paymentRecord.component);
  const rawTiming =
    paymentRecord.paymentTiming ??
    paymentRecord.payment_timing ??
    componentRecord.paymentTiming ??
    componentRecord.payment_timing;
  const timingString =
    typeof rawTiming === "string"
      ? rawTiming
      : rawTiming == null
        ? ""
        : String(rawTiming);
  return normalizeTimingKey(timingString);
};

type TemplatePaymentLabels = {
  fieldLabels: Record<string, string>;
  optionLabels: Record<string, Record<string, string>>;
};

const emptyTemplatePaymentLabels: TemplatePaymentLabels = {
  fieldLabels: {},
  optionLabels: {},
};

const getTemplateStepId = (step: unknown): string => {
  const stepRecord = asRecord(step);
  const rawTitle = stepRecord.title;
  const titleKey = String(
    rawTitle == null ? "step" : rawTitle,
  )
    .toLowerCase()
    .replace(/\s+/g, "_");
  const stepIdValue =
    stepRecord.stepId || stepRecord.id || stepRecord.key;
  if (stepIdValue != null && stepIdValue !== "") {
    return String(stepIdValue);
  }
  return titleKey;
};

const getOptionValue = (option: unknown): string => {
  if (typeof option === "string") return option;
  const optionRecord = asRecord(option);
  const rawValue =
    optionRecord.value ?? optionRecord.key ?? optionRecord.id ?? "";
  return String(rawValue);
};

const getOptionText = (option: unknown): string => {
  if (typeof option === "string") return option;
  const optionRecord = asRecord(option);
  const rawText =
    optionRecord.label ??
    optionRecord.title ??
    optionRecord.text ??
    optionRecord.name ??
    optionRecord.value ??
    optionRecord.key ??
    "";
  return String(rawText);
};

const addOptionLabel = (
  labels: TemplatePaymentLabels,
  fieldPath: string,
  value: string,
  label: string,
) => {
  if (!value || !label) return;
  if (!labels.optionLabels[fieldPath]) labels.optionLabels[fieldPath] = {};
  labels.optionLabels[fieldPath][value] = label;
  labels.optionLabels[fieldPath][value.toLowerCase()] = label;
  labels.optionLabels[fieldPath][value.toUpperCase()] = label;
};

const buildTemplatePaymentLabels = (
  templateJson: unknown,
): TemplatePaymentLabels => {
  const templateRecord = asRecord(templateJson);
  const steps = Array.isArray(templateRecord.steps)
    ? templateRecord.steps
    : [];
  if (steps.length === 0) return emptyTemplatePaymentLabels;

  const labels: TemplatePaymentLabels = {
    fieldLabels: {},
    optionLabels: {},
  };

  const registerField = (field: unknown, fieldPath: string, stepId: string) => {
    const fieldRecord = asRecord(field);
    const fieldLabel =
      fieldRecord.label ?? fieldRecord.title ?? fieldRecord.name;
    if (fieldLabel != null && fieldLabel !== "") {
      labels.fieldLabels[fieldPath] = String(fieldLabel);
    }

    if (Array.isArray(fieldRecord.options)) {
      fieldRecord.options.forEach((option: unknown) => {
        addOptionLabel(
          labels,
          fieldPath,
          getOptionValue(option),
          getOptionText(option),
        );
      });
    }

    if (Array.isArray(fieldRecord.fields)) {
      fieldRecord.fields.forEach((nestedField: unknown) => {
        const nestedRecord = asRecord(nestedField);
        const nestedKey = String(nestedRecord.key || "");
        if (!nestedKey) return;
        registerField(nestedField, `${fieldPath}.${nestedKey}`, stepId);
      });
    }
  };

  steps.forEach((step: unknown) => {
    const stepId = getTemplateStepId(step);
    const stepRecord = asRecord(step);
    if (!Array.isArray(stepRecord.fields)) return;

    stepRecord.fields.forEach((field: unknown) => {
      const fieldRecord = asRecord(field);
      const fieldKey = String(fieldRecord.key || "");
      if (!fieldKey) return;
      registerField(field, `${stepId}.${fieldKey}`, stepId);
    });
  });

  return labels;
};

interface BreakdownItem {
  componentType: string;
  calculationMode: string;
  rawValue: number;
  resolvedAmount: number;
}

interface ComponentPaymentsTabProps {
  deal: DealResponseDto | null;
  formData: DealFormData | null;
  isFighter: boolean;
  canPayComponents: boolean;
  handlePayComponent: (componentPaymentId: number) => void | Promise<void>;
  t: TranslationFn;
}

const ComponentPaymentsTab: React.FC<ComponentPaymentsTabProps> = ({
  deal,
  formData,
  isFighter,
  canPayComponents,
  handlePayComponent,
  t,
}) => {
  const [expandedTimings, setExpandedTimings] = useState<Record<string, boolean>>({});
  const [hoveredInfo, setHoveredInfo] = useState<string | null>(null);
  const [loadingPayments, setLoadingPayments] = useState<Record<number, boolean>>({});
  const currency = String(asRecord(asRecord(formData).core).currency || "");

  const templateLabels = buildTemplatePaymentLabels(deal?.templateJson);

  const getTemplateOptionLabel = (
    fieldPath: string,
    value?: string | null,
  ): string | undefined => {
    const normalizedValue = String(value || "");
    if (!normalizedValue) return undefined;

    const fieldLabels = templateLabels.optionLabels[fieldPath];
    return (
      fieldLabels?.[normalizedValue] ||
      fieldLabels?.[normalizedValue.toLowerCase()] ||
      fieldLabels?.[normalizedValue.toUpperCase()]
    );
  };

  const paymentsByTiming: Record<string, DealComponentPaymentResponseDto[]> = {};
  (deal?.componentPayments || []).forEach((payment) => {
    const timing = resolvePaymentTiming(payment);
    if (!paymentsByTiming[timing]) paymentsByTiming[timing] = [];
    paymentsByTiming[timing].push(payment);
  });

  const paymentTimingsToRender: string[] = (() => {
    const availableTimings = Object.keys(paymentsByTiming);

    if (availableTimings.length > 0) {
      const hasPreEvent = availableTimings.includes("pre_event");
      const hasPostEvent = availableTimings.includes("post_event");
      if (hasPreEvent && hasPostEvent) return ["pre_event", "post_event"];
      return availableTimings;
    }

    const paymentType = (deal?.paymentType || "").toUpperCase();
    if (paymentType === "SPLIT") return ["pre_event", "post_event"];
    if (paymentType === "UPFRONT") return ["pre_event"];

    return ["single"];
  })();

  const isSplitPayment = paymentTimingsToRender.length > 1;

  const getTimingLabel = (timingKey: string): string => {
    const templateTimingLabel = getTemplateOptionLabel(
      "components.fundingStage",
      timingKey,
    );
    if (!isSplitPayment) return t("payment") || "Payment";
    if (timingKey === "pre_event") {
      return templateTimingLabel || t("preEvent") || "Pre-Event";
    }
    if (timingKey === "post_event") {
      return templateTimingLabel || t("postEvent") || "Post-Event";
    }
    return toTitleCase(timingKey);
  };

  const getBreakdownItems = (payment: unknown, timingKey: string): BreakdownItem[] => {
    if (!payment) return [];
    const paymentRecord = asRecord(payment);
    const rawItems =
      timingKey === "pre_event" || timingKey === "single"
        ? paymentRecord.preMatchBreakdown
        : timingKey === "post_event"
          ? paymentRecord.postMatchBreakdown
          : paymentRecord.preMatchBreakdown || paymentRecord.postMatchBreakdown;
    if (!Array.isArray(rawItems)) return [];
    return rawItems as BreakdownItem[];
  };

  const getComponentTypeLabel = (componentType: string): string => {
    if (componentType === "BASE_AMOUNT") {
      return templateLabels.fieldLabels["core.baseAmount"] || t("baseAmount") || "Base Amount";
    }

    return (
      getTemplateOptionLabel("components.componentType", componentType) ||
      toTitleCase(componentType)
    );
  };

  const handlePayClick = async (paymentId: number) => {
    if (loadingPayments[paymentId]) return;

    try {
      setLoadingPayments((prev) => ({ ...prev, [paymentId]: true }));
      await Promise.resolve(handlePayComponent(paymentId));
    } finally {
      setLoadingPayments((prev) => ({ ...prev, [paymentId]: false }));
    }
  };

  const getComponentPillClass = (componentType: string) => {
    const lower = String(componentType || "").toLowerCase();
    if (lower.includes("base")) {
      return "bg-[#F0F6FE] text-gray-900";
    }
    if (lower.includes("bonus") || lower.includes("win")) {
      return "bg-[#EAF8EE] text-gray-900";
    }
    if (lower.includes("expense") || lower.includes("travel") || lower.includes("accommodation")) {
      return "bg-[#FFF0F0] text-gray-900";
    }
    if (lower.includes("penalty") || lower.includes("deduct")) {
      return "bg-[#FFF4E5] text-gray-900";
    }
    return "bg-gray-50 text-gray-900";
  };

  return (
    <div className="bg-white rounded-3xl p-4 sm:p-6 shadow-sm border border-gray-100 space-y-4 sm:space-y-6 text-left">
      <h2 className="text-xl font-bold text-gray-900 tracking-tight">
        {t("componentPlacements")}
      </h2>

      <div className="space-y-4">
        {paymentTimingsToRender.map((timingKey) => {
          const timingDisplayLabel = getTimingLabel(timingKey);
          const paymentsForTiming = paymentsByTiming[timingKey] || [];
          const isExpanded = !!expandedTimings[timingKey];

          const totalAmount = paymentsForTiming.reduce(
            (sum: number, payment: DealComponentPaymentResponseDto) => sum + Number(payment.amount || 0),
            0,
          );

          const hasPendingPayments = paymentsForTiming.some(
            (payment: DealComponentPaymentResponseDto) => payment.status === "PENDING",
          );

          const hasOnlyPaid =
            paymentsForTiming.length > 0 &&
            paymentsForTiming.every(
              (payment: DealComponentPaymentResponseDto) =>
                payment.status === "PAID" || payment.status === "COMPLETED",
            );

          const unpaidPayments = paymentsForTiming.filter(
            (payment: DealComponentPaymentResponseDto) => payment.status === "PENDING",
          );

          const hasCancelledPayments = paymentsForTiming.some(
            (payment: DealComponentPaymentResponseDto) => payment.status === "CANCELLED",
          );

          const canShowPayButton =
            hasPendingPayments && !isFighter && canPayComponents;

          return (
            <div
              key={timingKey}
              className="rounded-2xl border border-gray-100 bg-white p-4 sm:p-5 shadow-sm transition-all duration-200"
            >
              <div
                onClick={() =>
                  setExpandedTimings((prev) => ({
                    ...prev,
                    [timingKey]: !prev[timingKey],
                  }))
                }
                className="flex items-center justify-between cursor-pointer gap-3"
              >
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm sm:text-base font-bold text-gray-900">
                      {timingDisplayLabel}
                    </span>

                    {hasOnlyPaid && paymentsForTiming.length > 0 && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#EAF8EE] text-[#1F8E3D]">
                        {t("paymentStatusPaid") || "Paid"}
                      </span>
                    )}

                    {hasPendingPayments && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                        {t("paymentStatusPending") || "Pending"}
                      </span>
                    )}

                    {hasCancelledPayments && (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                        {t("paymentStatusCancelled") || "Cancelled"}
                      </span>
                    )}

                    {timingKey === "post_event" && (
                      <div
                        className="relative flex items-center shrink-0"
                        onMouseEnter={() => setHoveredInfo(timingKey)}
                        onMouseLeave={() => setHoveredInfo(null)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600" />

                        {hoveredInfo === timingKey && (
                          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-900 text-white text-[11px] px-2 py-1 rounded-md shadow-lg z-50">
                            {t("postEventPaymentNote") ||
                              "Note: As per the result policies, the amount will change."}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {paymentsForTiming[0]?.dueOn && (
                    <p className="text-xs text-gray-400 mt-1">
                      {t("dueDate") || "Due date"}: {paymentsForTiming[0].dueOn}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-base sm:text-lg font-bold text-gray-900 tabular-nums">
                    {totalAmount} {currency}
                  </span>

                  {canShowPayButton &&
                    unpaidPayments.map((unpaidPayment: DealComponentPaymentResponseDto) => {
                      const paymentId = unpaidPayment.id;
                      if (paymentId == null) return null;
                      const isLoading = !!loadingPayments[paymentId];

                      return (
                        <button
                          key={paymentId}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handlePayClick(paymentId);
                          }}
                          disabled={isLoading}
                          className={`text-xs font-semibold px-3 py-1 rounded-full text-white transition-all flex items-center justify-center gap-1.5 ${
                            isLoading
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-emerald-500 hover:bg-emerald-600 active:scale-95"
                          }`}
                        >
                          {isLoading ? (
                            <>
                              <FaSpinner className="animate-spin h-3 w-3" />
                              {t("processing") || "Processing..."}
                            </>
                          ) : (
                            t("Pay") || "Pay"
                          )}
                        </button>
                      );
                    })}

                  <div
                    className={`w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 transition-transform duration-200 shrink-0 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  >
                    <ChevronDownIcon className="w-3 h-3" />
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  {paymentsForTiming.length > 0 ? (
                    paymentsForTiming.map((payment: DealComponentPaymentResponseDto) => {
                      const breakdownItems = getBreakdownItems(payment, timingKey);

                      return (
                        <div key={payment.id} className="space-y-3">
                          {breakdownItems.length > 0 ? (
                            breakdownItems.map((item: BreakdownItem, index: number) => (
                              <div
                                key={index}
                                className={`flex items-center justify-between px-5 py-3.5 rounded-2xl ${getComponentPillClass(item.componentType)}`}
                              >
                                <div className="text-sm font-medium text-gray-800 truncate pr-2">
                                  {getComponentTypeLabel(item.componentType)}
                                </div>
                                <div className="text-sm font-bold text-gray-900 tabular-nums shrink-0">
                                  {item.resolvedAmount} {currency}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                              {t("noBreakdownAvailable") || "No breakdown available"}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                      {t("noPaymentsForTiming") || "No payments for this timing"}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {(!deal?.componentPayments || deal?.componentPayments.length === 0) && (
        <div className="text-sm text-gray-400 text-center py-10 border border-dashed border-gray-200 rounded-2xl bg-white">
          {t("noComponentPayments") || "No component payments"}
        </div>
      )}
    </div>
  );
};

export default ComponentPaymentsTab;
